/**
 * campaignsService — capa de datos del dashboard de Campañas Digitales.
 *
 * Prueba de concepto: simula cómo se alimentarían los datos en el futuro.
 * Cada mes el equipo de marketing entrega un Excel ("Reporte de Resultados
 * Campaña Digital CLMX"). Aquí los leemos TAL CUAL desde /public, los parseamos
 * en el navegador con `xlsx`, normalizamos el esquema (Enero trae un layout
 * distinto a los demás) y entregamos un dataset homogéneo al dashboard.
 *
 * Para sumar un mes nuevo en producción basta con:
 *   1) copiar el .xlsx a public/marketing_reports/
 *   2) agregar una línea a MONTHLY_FEED
 * El dashboard lo toma automáticamente.
 */
import * as XLSX from 'xlsx';
import { metaApi } from './metaService';
import { integrationsApi } from './integrationsService';

const BASE = `${process.env.PUBLIC_URL || ''}/marketing_reports`;

// ───────────────────────────────────────────────────────────────────────────
// El "feed mensual". Orden cronológico. Agregar un mes = agregar una línea.
// ───────────────────────────────────────────────────────────────────────────
export const MONTHLY_FEED = [
  { key: '2025-11', month: 'Noviembre', year: 2025, order: 1, file: '2025-11-noviembre.xlsx' },
  { key: '2025-12', month: 'Diciembre', year: 2025, order: 2, file: '2025-12-diciembre.xlsx' },
  { key: '2026-01', month: 'Enero',     year: 2026, order: 3, file: '2026-01-enero.xlsx' },
  { key: '2026-02', month: 'Febrero',   year: 2026, order: 4, file: '2026-02-febrero.xlsx' },
  { key: '2026-03', month: 'Marzo',     year: 2026, order: 5, file: '2026-03-marzo.xlsx' },
  { key: '2026-04', month: 'Abril',     year: 2026, order: 6, file: '2026-04-abril.xlsx' },
  { key: '2026-05', month: 'Mayo',      year: 2026, order: 7, file: '2026-05-mayo.xlsx' },
  { key: '2026-06', month: 'Junio',     year: 2026, order: 8, file: '2026-06-junio.xlsx' },
  { key: '2026-07', month: 'Julio',     year: 2026, order: 9, file: '2026-07-julio.xlsx' },
  { key: '2026-08', month: 'Agosto',    year: 2026, order: 10, file: '2026-08-agosto.xlsx' },
];

export const PLATFORM_COLORS = {
  Meta:    '#0866FF',
  TikTok:  '#111827',
  YouTube: '#FF0033',
  Banners: '#F68D2E',
  Otros:   '#94a3b8',
};

export const OBJECTIVE_COLORS = {
  'Alcance':     '#073C5C',
  'Interacción': '#F68D2E',
  'Tráfico':     '#10b981',
  'Otros':       '#94a3b8',
};

// ───────────────────────────────────────────────────────────────────────────
// Helpers de normalización
// ───────────────────────────────────────────────────────────────────────────
const stripAccents = (s) =>
  String(s).normalize('NFD').replace(/[\u0300-\u036f]/g, '');

function toNum(v) {
  if (v === '' || v === null || v === undefined) return null;
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  const n = Number(String(v).replace(/[, ]/g, ''));
  return Number.isFinite(n) ? n : null;
}

function normalizePlatform(raw) {
  const s = stripAccents(String(raw)).trim().toLowerCase();
  if (!s) return 'Otros';
  if (s.includes('meta') || s.includes('face') || s.includes('insta')) return 'Meta';
  if (s.includes('tiktok') || s.includes('tik tok')) return 'TikTok';
  if (s.includes('youtube')) return 'YouTube';
  if (s.includes('banner') || s.includes('display') || s.includes('programatic')) return 'Banners';
  return 'Otros';
}

// Agrupa los objetivos heterogéneos del Excel en familias comparables.
function normalizeObjective(raw) {
  const s = stripAccents(String(raw)).trim().toLowerCase();
  if (!s) return 'Otros';
  if (s.includes('interac')) return 'Interacción';
  if (s.includes('trafico')) return 'Tráfico';
  if (s.includes('alcance') || s.includes('awareness')) return 'Alcance';
  return 'Otros';
}

function cleanText(v) {
  return String(v ?? '').replace(/\s+/g, ' ').trim();
}

function fmtCellDate(v) {
  if (v instanceof Date && !isNaN(v)) {
    return v.toISOString().slice(0, 10);
  }
  return cleanText(v);
}

// ───────────────────────────────────────────────────────────────────────────
// Parser de un workbook (un mes)
// ───────────────────────────────────────────────────────────────────────────
function findHeaderRow(rows) {
  return rows.findIndex((r) =>
    Array.isArray(r) && r.some((c) => stripAccents(c).trim().toLowerCase() === 'plataforma')
  );
}

// Busca una etiqueta tipo "Fecha de corte" en cualquier celda y devuelve el
// siguiente valor no vacío de su misma fila (el layout varía entre meses).
function findMetaValue(rows, label) {
  const target = stripAccents(label).trim().toLowerCase();
  for (const r of rows) {
    if (!Array.isArray(r)) continue;
    for (let i = 0; i < r.length; i++) {
      if (stripAccents(r[i]).trim().toLowerCase() === target) {
        for (let j = i + 1; j < r.length; j++) {
          if (r[j] !== '' && r[j] !== null && r[j] !== undefined) return r[j];
        }
      }
    }
  }
  return null;
}

function parseWorkbook(wb, feed) {
  const ws = wb.Sheets[wb.SheetNames[0]];
  const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '', raw: true });

  const headerIdx = findHeaderRow(rows);
  const records = [];
  if (headerIdx === -1) return { records, meta: buildMonthMeta(feed, null, 0) };

  // Mapa nombre-de-columna → índice (tolerante a acentos / espacios).
  const colMap = {};
  rows[headerIdx].forEach((c, i) => {
    const key = stripAccents(c).trim().toLowerCase();
    if (key && !(key in colMap)) colMap[key] = i;
  });
  const idx = (name) => {
    const k = stripAccents(name).trim().toLowerCase();
    return k in colMap ? colMap[k] : -1;
  };
  const cell = (row, name) => {
    const i = idx(name);
    return i === -1 ? '' : row[i];
  };

  for (let r = headerIdx + 1; r < rows.length; r++) {
    const row = rows[r];
    if (!Array.isArray(row)) continue;
    const platformRaw = cleanText(cell(row, 'Plataforma'));
    const campaign = cleanText(cell(row, 'Campaña'));
    // La fila de totales y las vacías no traen plataforma+campaña → se omiten.
    if (!platformRaw || !campaign) continue;

    const objectiveRaw = cleanText(cell(row, 'Objetivo'));
    records.push({
      monthKey: feed.key,
      month: feed.month,
      order: feed.order,
      year: feed.year,
      platform: normalizePlatform(platformRaw),
      platformRaw,
      campaign,
      objective: normalizeObjective(objectiveRaw),
      objectiveRaw,
      buyType: cleanText(cell(row, 'Tipo de Compra')).toUpperCase(),
      start: fmtCellDate(cell(row, 'Fecha de Inicio')),
      end: fmtCellDate(cell(row, 'Fecha Finalización')),
      costPlanned: toNum(cell(row, 'Costo Planeado')),
      costReal: toNum(cell(row, 'Costo Real')),
      interactions: toNum(cell(row, 'Interacciones')),
      impressions: toNum(cell(row, 'Impresiones')),
      reach: toNum(cell(row, 'Alcance')),
      clicks: toNum(cell(row, 'Clics')),
      ctr: toNum(cell(row, 'CTR')),
      views: toNum(cell(row, 'Views')),
      vtr: toNum(cell(row, 'VTR')),
      er: toNum(cell(row, 'ER')),
      spend: toNum(cell(row, 'Gasto')),
      spendPlanned: toNum(cell(row, 'Gasto Planeado')),
      index: toNum(cell(row, 'Index')),
      status: cleanText(cell(row, 'Estatus')) || 'Activa',
    });
  }

  const cutoffRaw = findMetaValue(rows, 'Fecha de corte');
  return { records, meta: buildMonthMeta(feed, cutoffRaw, records.length) };
}

function buildMonthMeta(feed, cutoffRaw, count) {
  let cutoff = cutoffRaw ? fmtCellDate(cutoffRaw) : null;
  // Algunos reportes traen typo de año en la fecha de corte (p.ej. Noviembre
  // 2025 dice "2026-12-01"). La alineamos al año real del periodo (del feed).
  if (cutoff && /^\d{4}-/.test(cutoff) && !cutoff.startsWith(feed.year + '-')) {
    cutoff = feed.year + cutoff.slice(4);
  }
  return {
    key: feed.key,
    month: feed.month,
    year: feed.year,
    order: feed.order,
    file: feed.file,
    cutoff,
    rows: count,
  };
}

// ───────────────────────────────────────────────────────────────────────────
// Carga pública: trae los 5 reportes en paralelo y entrega el dataset unificado
// ───────────────────────────────────────────────────────────────────────────
export async function loadCampaignReports() {
  const settled = await Promise.allSettled(
    MONTHLY_FEED.map(async (feed) => {
      const url = `${BASE}/${encodeURIComponent(feed.file)}`;
      const resp = await fetch(url);
      if (!resp.ok) throw new Error(`HTTP ${resp.status} al cargar ${feed.file}`);
      const buf = await resp.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array', cellDates: true });
      return parseWorkbook(wb, feed);
    })
  );

  const records = [];
  const months = [];
  const errors = [];
  settled.forEach((res, i) => {
    if (res.status === 'fulfilled') {
      records.push(...res.value.records);
      months.push(res.value.meta);
    } else {
      errors.push({ month: MONTHLY_FEED[i].month, error: String(res.reason?.message || res.reason) });
    }
  });
  months.sort((a, b) => a.order - b.order);

  return { records, months, errors, brand: 'Cantabria Labs' };
}

// ───────────────────────────────────────────────────────────────────────────
// Fuente EN VIVO: Marketing API de Meta (Ads Insights) vía backend.
// Devuelve el MISMO dataset unificado que loadCampaignReports (Excel), para que
// el tablero de Campañas lo consuma sin cambios. El backend ya normaliza al
// esquema de records + months (ver meta_client.normalize_ads_insights).
// ───────────────────────────────────────────────────────────────────────────
export async function loadCampaignReportsFromMeta(username, dateFrom, dateTo, account, fresh = false) {
  if (!username) {
    return { records: [], months: [], errors: [{ month: 'Meta', error: 'Falta el usuario para consultar Meta.' }], brand: 'Cantabria Labs' };
  }
  try {
    const res = await metaApi.adsInsights(username, dateFrom, dateTo, 'campaign', account, fresh);
    const months = (res.months || []).sort((a, b) => a.order - b.order);
    return {
      records: res.records || [],
      months,
      errors: res.errors || [],
      notices: res.notices || [],
      brand: res.brand || 'Cantabria Labs',
    };
  } catch (e) {
    // Propagamos como error de carga para que la página muestre el estado claro.
    throw new Error(e?.message || 'No se pudieron cargar las campañas desde Meta.');
  }
}

// ───────────────────────────────────────────────────────────────────────────
// Fuente MULTI-RED: todas las plataformas de ads en un solo dataset.
// Usa el mismo endpoint de insights (que en el tenant demo devuelve las campañas
// de Meta + TikTok + YouTube + Banners juntas, con la plataforma en cada record).
// Devuelve el MISMO esquema {records, months, brand} que las demás fuentes.
// ───────────────────────────────────────────────────────────────────────────
export async function loadCampaignReportsAll(username, dateFrom, dateTo) {
  if (!username) {
    return { records: [], months: [], errors: [{ month: 'Ads', error: 'Falta el usuario para consultar las campañas.' }], brand: 'Data Bunker' };
  }
  try {
    const res = await metaApi.adsInsights(username, dateFrom, dateTo);
    const months = (res.months || []).sort((a, b) => a.order - b.order);
    return {
      records: res.records || [],
      months,
      errors: res.errors || [],
      brand: res.brand || 'Data Bunker',
    };
  } catch (e) {
    throw new Error(e?.message || 'No se pudieron cargar las campañas.');
  }
}

// ───────────────────────────────────────────────────────────────────────────
// Fuente EN VIVO: Business/Marketing API de TikTok Ads vía backend.
// Devuelve el MISMO dataset unificado que loadCampaignReportsFromMeta (el backend
// ya normaliza al esquema records + months, ver tiktok_client.normalize_tiktok_report),
// para que el tablero de Campañas lo consuma sin cambios (plataforma 'TikTok').
// ───────────────────────────────────────────────────────────────────────────
export async function loadCampaignReportsFromTikTok(username, dateFrom, dateTo) {
  if (!username) {
    return { records: [], months: [], errors: [{ month: 'TikTok', error: 'Falta el usuario para consultar TikTok.' }], brand: 'Cantabria Labs' };
  }
  try {
    const res = await integrationsApi.tiktokCampaigns(username, dateFrom, dateTo);
    const months = (res.months || []).sort((a, b) => a.order - b.order);
    return {
      records: res.records || [],
      months,
      errors: res.errors || [],
      brand: res.brand || 'Cantabria Labs',
    };
  } catch (e) {
    throw new Error(e?.message || 'No se pudieron cargar las campañas desde TikTok.');
  }
}

// ───────────────────────────────────────────────────────────────────────────
// Agregación reutilizable
// ───────────────────────────────────────────────────────────────────────────
const sum = (arr, sel) => arr.reduce((a, r) => a + (sel(r) || 0), 0);

export function summarize(records) {
  const spend = sum(records, (r) => r.spend);
  const spendPlanned = sum(records, (r) => r.spendPlanned);
  const impressions = sum(records, (r) => r.impressions);
  const reach = sum(records, (r) => r.reach);
  const clicks = sum(records, (r) => r.clicks);
  const interactions = sum(records, (r) => r.interactions);
  const views = sum(records, (r) => r.views);
  return {
    spend,
    spendPlanned,
    impressions,
    reach,
    clicks,
    interactions,
    views,
    campaigns: new Set(records.map((r) => r.campaign)).size,
    lines: records.length,
    // CTR ponderado real = clics / impresiones (no promedio de promedios)
    ctr: impressions ? clicks / impressions : 0,
    // Pacing: cuánto del presupuesto se ejecutó (1 = exacto)
    pacing: spendPlanned ? spend / spendPlanned : 0,
    // CPM efectivo de toda la inversión
    cpm: impressions ? (spend / impressions) * 1000 : 0,
    // Costo por clic efectivo
    cpc: clicks ? spend / clicks : 0,
  };
}

// Agrupa registros por una clave y resume cada grupo.
export function groupBy(records, keySel) {
  const map = new Map();
  for (const r of records) {
    const k = keySel(r);
    if (!map.has(k)) map.set(k, []);
    map.get(k).push(r);
  }
  return Array.from(map.entries()).map(([key, rows]) => ({
    key,
    rows,
    ...summarize(rows),
  }));
}
